import { MouseEvent } from 'react';
import cn from 'classnames';

import { useCtxHistory } from 'hooks';
import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import { getSalaryReportProfile } from 'services/authService';
import { openSignUpModal } from 'store/modals/actions';
import { POPUP_SURVEY_SOURCE, REPORT_SALARY_SURVEY_FROM_MATCHES } from 'utils/constant';

import { useMobileNavCtx } from '../mobileNav';

import classes from './index.module.css';

interface Link {
  text: string;
  href: string;
}

export interface TitleListProps {
  title: string;
  links: Link[];
  iconList?: undefined;
}

export interface CommonListProps {
  links: Link[];
  title?: undefined;
  iconList?: undefined;
}

export interface IconListProps {
  iconList?: boolean;
  links: Link[] | (Link & {
    icon: string;
    color: string;
  })[];
  title?: undefined;
}

export type ListProps = TitleListProps | CommonListProps | IconListProps;

export const TitleList = ({ title, links }: TitleListProps) => {
  const mobileNavCtx = useMobileNavCtx();

  const onLinkClick = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    const pathname = href.split('#')?.[0] || '';
    if (window?.location?.pathname === pathname) {
      mobileNavCtx?.onLinkClick?.(e);
    }
  };

  return (
    <div className={cn('h-100', classes.container)}>
      <span className={cn('z-m-0 z-p-0 z-font-14 text-uppercase', classes.title)}>{title}</span>
      <ul className={cn('z-p-0 z-m-0', classes.list)}>
        {links.map(({ text, href }) => (
          <li key={`head-link-${text}-${href}`} className="z-mt-14">
            <a
              className={cn('text-capitalize', classes.link)}
              href={href}
              onClick={(e) => onLinkClick(e, href)}
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const IconList = ({ links }: IconListProps) => {
  const dispatch = useAppDispatch();
  const history = useCtxHistory();
  const isLoggedIn = useAppSelector(({ user }) => user.isLoggedIn);
  const mobileNavCtx = useMobileNavCtx();

  const onLinkClick = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href === '/matches/' && !isLoggedIn) {
      e.preventDefault();

      const query = {
        survey_step: 'step1',
        survey: '036',
        utm_campaign: 'nav_sign_up',
      };
      dispatch(
        openSignUpModal({
          ...query,
          popupType: 'header-sign-up',
        })
      );
      history.pushQuery(query);
    }
    if (window?.location?.href === href) {
      mobileNavCtx?.onLinkClick?.(e);
    }
  };

  return (
    <div className={cn('h-100', classes.container, classes.commonList)}>
      <ul className={cn('z-p-0 z-m-0', classes.list)}>
        {links.map(({ icon, color, text, href }) => (
          <li key={`head-link-${text}-${href}`}>
            <a
              href={href}
              className={cn('text-capitalize d-flex', classes.link)}
              onClick={(e) => onLinkClick(e, href)}
            >
              <span
                className={cn(
                  'z-br-4 z-mr-12 d-flex align-items-center justify-content-center',
                  classes.icon
                )}
                style={{ backgroundColor: color }}
              >
                <img src={icon} width={24} height={24} />
              </span>
              {text}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const CommonList = ({ links }: CommonListProps) => {
  const dispatch = useAppDispatch();
  const history = useCtxHistory();
  const mobileNavCtx = useMobileNavCtx();

  const onLinkClick = async (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href === '/matches/salary/') {
      e.preventDefault();

      const reportProfile = await getSalaryReportProfile();

      if (!reportProfile?.length) {
        mobileNavCtx?.onLinkClick?.(e);
        const query = {
          survey_step: 'step1',
          survey: REPORT_SALARY_SURVEY_FROM_MATCHES,
          src: POPUP_SURVEY_SOURCE.MATCHES_PAGE,
          skipQuestion: [8],
        };
        dispatch(
          openSignUpModal(
            {
              ...query,
              popupType: 'career-callout-sign-up',
            },
            true
          )
        );
        history.pushQuery(query);
      } else {
        window.location.href = href;
      }
    }
    if (window?.location?.href === href) {
      mobileNavCtx?.onLinkClick?.(e);
    }
  };
  return (
    <div className={cn('h-100', classes.container, classes.commonList)}>
      <ul className={cn('z-p-0 z-m-0', classes.list)}>
        {links.map(({ text, href }) => (
          <li key={`head-link-${text}-${href}`} className={cn('position-relative', classes.item)}>
            <a
              className={cn('text-capitalize', classes.link)}
              href={href}
              onClick={(e) => onLinkClick(e, href)}
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};
